import { Badge, Button, Card, CardHeader, Drawer, Table, formatDate, type Column } from "@helmr/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchCategories, fetchExpense, fetchExpenses, fetchPayees, type Attachment, type Expense } from "../../api/expenses";
import { downloadCsv, fetchBlobUrl } from "../../api/reports";
import { useServices } from "../../app/services";
import { Empty, Field, Gate, PageHead, css as s, errorText, isDate, monthOf, useScope, type Scope } from "../payroll/kit";

const PAGE = 50;

export const lastDayOfMonth = (month: string) => {
  const [y, m] = month.split("-").map(Number) as [number, number];
  return `${month}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, "0")}`;
};

/** `/expenses`: this month by default; filter by dates, category and status; cursor paging. The API gives no total, so none is shown. */
export function ExpensesPage() {
  const sc = useScope();
  const { client, session } = useServices();
  const [from, setFrom] = useState(`${monthOf(sc.today)}-01`);
  const [to, setTo] = useState(lastDayOfMonth(monthOf(sc.today)));
  const [categoryId, setCategoryId] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [exportErr, setExportErr] = useState<string | null>(null);
  const valid = isDate(from) && isDate(to) && from <= to;

  // Archived ones too: old expenses still point at them. The filter below offers only live categories.
  const cats = useQuery({ queryKey: ["expenses", "categories-all", ...sc.scopeKey], queryFn: () => fetchCategories(client, true), retry: false });
  const payees = useQuery({ queryKey: ["expenses", "payees-all", ...sc.scopeKey], queryFn: () => fetchPayees(client, true), retry: false });
  const catName = new Map((cats.data?.categories ?? []).map((c) => [c.id, c.name]));
  const payeeNames = new Map((payees.data?.payees ?? []).map((p) => [p.id, p.name]));
  /** An expense saved against a payee holds only its id; `payeeName` is the free-text alternative. */
  const payeeOf = (e: Pick<Expense, "payeeId" | "payeeName">) => (e.payeeId ? payeeNames.get(e.payeeId) : undefined) ?? e.payeeName ?? "—";
  const q = useInfiniteQuery({
    queryKey: ["expenses", "list", from, to, categoryId, status, ...sc.scopeKey],
    queryFn: ({ pageParam }) => fetchExpenses(client, { from, to, limit: PAGE, branchId: sc.branchId, ...(categoryId ? { categoryId } : {}), ...(status ? { status } : {}), ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: "" as string,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: valid, retry: false,
  });
  const rows = q.data?.pages.flatMap((p) => p.expenses) ?? [];
  const canExport = sc.ctx.has("exports.expenses");
  const exportCsv = async () => { setExportErr(null); try { await downloadCsv(session, "/expenses/export", { from, to, branchId: sc.branchId, categoryId: categoryId || undefined, status: status || undefined }, "expenses.csv"); } catch (e) { setExportErr(errorText(e)); } };

  const cols: Array<Column<Expense>> = [
    { key: "d", header: "Date", cell: (e) => formatDate(e.expenseDate, sc.locale, "short") },
    { key: "c", header: "Category", cell: (e) => <span className={s.truncate} title={catName.get(e.categoryId)}>{catName.get(e.categoryId) ?? "—"}</span> },
    { key: "p", header: "Paid to", cell: (e) => <span className={s.truncate} title={payeeOf(e)}>{payeeOf(e)}</span> },
    { key: "m", header: "Mode", cell: (e) => e.paymentMode.replace(/_/g, " ") },
    { key: "s", header: "Status", cell: (e) => (e.voidedAt ? <Badge tone="crit">Void</Badge> : <Badge tone={e.status === "paid" ? "ok" : "warn"} dot>{e.status === "paid" ? "Paid" : "Unpaid"}</Badge>) },
    { key: "t", header: "Total", align: "end", cell: (e) => <b className={s.num}>{sc.money(e.total)}</b> },
  ];

  return (
    <div className={s.page}>
      <PageHead title="Expenses" sub="Rent, utilities and everything else you spend.">
        {canExport ? <Button size="sm" onClick={() => void exportCsv()} disabled={!valid}>Export CSV</Button> : null}
        {sc.ctx.has("expenses.import") ? <Button as={Link} to="/expenses/import" size="sm">Import statement</Button> : null}
        {sc.ctx.has("expenses.add") ? <Button as={Link} to="/expenses/new" variant="primary" size="sm">Add expense</Button> : null}
      </PageHead>
      <Card aria-label="Expenses">
        <CardHeader title="Expenses" />
        <div className={s.tools}>
          <Field label="From" error={isDate(from) ? null : "YYYY-MM-DD"}>{(a) => <input {...a} className={s.input} value={from} onChange={(e) => setFrom(e.target.value)} />}</Field>
          <Field label="To" error={isDate(to) ? (from <= to ? null : "Before the start date") : "YYYY-MM-DD"}>{(a) => <input {...a} className={s.input} value={to} onChange={(e) => setTo(e.target.value)} />}</Field>
          <Field label="Category">{(a) => <select {...a} className={s.input} value={categoryId} onChange={(e) => setCategoryId(e.target.value)}><option value="">All</option>{(cats.data?.categories ?? []).filter((c) => !c.archived).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>}</Field>
          <Field label="Status">{(a) => <select {...a} className={s.input} value={status} onChange={(e) => setStatus(e.target.value)}><option value="">All</option><option value="paid">Paid</option><option value="unpaid">Unpaid</option></select>}</Field>
        </div>
        {exportErr ? <p className={s.formErr} role="alert">{exportErr}</p> : null}
        {!valid ? <Empty icon="receipt" title="Check the dates" description="Enter a start and end date, start first." /> : (
          <Gate query={q} what="expenses">
            {() => rows.length === 0 ? <Empty icon="receipt" title="No expenses this month" description="Nothing matches these filters." action={sc.ctx.has("expenses.add") ? <Button as={Link} to="/expenses/new" variant="primary">Add expense</Button> : undefined} /> : (
              <Table label="Expenses list" columns={cols} rows={rows} rowKey={(e) => e.id} onRowClick={(e) => setOpen(e.id)} minWidth={620} summary={`${rows.length} shown`} hasMore={q.hasNextPage} onLoadMore={() => void q.fetchNextPage()} loadingMore={q.isFetchingNextPage} />
            )}
          </Gate>
        )}
      </Card>
      {open ? <ExpenseDrawer id={open} sc={sc} catName={catName} payeeOf={payeeOf} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

function ExpenseDrawer({ id, sc, catName, payeeOf, onClose }: { id: string; sc: Scope; catName: Map<string, string>; payeeOf: (e: Pick<Expense, "payeeId" | "payeeName">) => string; onClose: () => void }) {
  const { client } = useServices();
  const q = useQuery({ queryKey: ["expenses", "one", id, ...sc.scopeKey], queryFn: () => fetchExpense(client, id), retry: false });
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()} title="Expense">
      <div className={s.drawerPad}><Gate query={q} what="this expense">
        {({ expense: e, attachments }) => (
          <div className={s.rows}>
            <ul className={s.list} aria-label="Expense details">
              <li><span>Date</span><span>{formatDate(e.expenseDate, sc.locale, "full")}</span></li>
              <li><span>Category</span><span>{catName.get(e.categoryId) ?? "—"}</span></li>
              <li><span>Paid to</span><span>{payeeOf(e)}</span></li>
              <li><span>Amount before tax</span><span className={s.num}>{sc.money(e.amountExTax)}</span></li>
              {e.taxTotal ? <li><span>Tax</span><span className={s.num}>{sc.money(e.taxTotal)}</span></li> : null}
              <li><span>Total</span><b className={s.num}>{sc.money(e.total)}</b></li>
              <li><span>Mode</span><span>{e.paymentMode.replace(/_/g, " ")}</span></li>
              <li><span>Status</span><span>{e.voidedAt ? "Void" : e.status === "paid" ? `Paid${e.paidOn ? ` on ${formatDate(e.paidOn, sc.locale, "short")}` : ""}` : "Unpaid"}</span></li>
              {e.supplierInvoiceNo ? <li><span>Supplier invoice</span><span>{e.supplierInvoiceNo}</span></li> : null}
              {e.reference ? <li><span>Reference</span><span>{e.reference}</span></li> : null}
              {e.note ? <li><span>Note</span><span>{e.note}</span></li> : null}
            </ul>
            <AttachmentList expenseId={e.id} items={attachments} />
          </div>
        )}
      </Gate></div>
    </Drawer>
  );
}

/** Attachments are fetched with the bearer token, shown from a short-lived object URL and released on close; nothing is stored in the browser. */
export function AttachmentList({ expenseId, items }: { expenseId: string; items: Attachment[] }) {
  if (items.length === 0) return <p className={s.note}>No attachments.</p>;
  return <ul className={s.rows} aria-label="Attachments">{items.map((a) => <li key={a.id}><AttachmentView expenseId={expenseId} a={a} /></li>)}</ul>;
}

function AttachmentView({ expenseId, a }: { expenseId: string; a: Attachment }) {
  const { session } = useServices();
  const isImage = a.contentType.startsWith("image/");
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const path = `/expenses/${expenseId}/attachments/${a.id}`;
  useEffect(() => {
    if (!isImage) return;
    let live: string | null = null;
    let cancelled = false;
    fetchBlobUrl(session, path).then((r) => { if (cancelled) URL.revokeObjectURL(r.url); else { live = r.url; setUrl(r.url); } }).catch((e) => setErr(errorText(e)));
    return () => { cancelled = true; if (live) URL.revokeObjectURL(live); };
  }, [isImage, path, session]);
  const open = async () => {
    try { const r = await fetchBlobUrl(session, path); window.open(r.url, "_blank", "noopener"); setTimeout(() => URL.revokeObjectURL(r.url), 60_000); } catch (e) { setErr(errorText(e)); }
  };
  return (
    <div className={s.rows}>
      <span title={a.filename} className={s.truncate}>{a.filename}</span>
      {isImage && url ? <img className={s.preview} src={url} alt={a.filename} /> : null}
      {!isImage ? <div><Button size="sm" onClick={() => void open()}>Open PDF</Button></div> : null}
      {err ? <p className={s.formErr} role="alert">{err}</p> : null}
    </div>
  );
}
